import { useEffect } from 'react'

import { createFileRoute, Link } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { useClerk } from '@clerk/react'
import z from 'zod'

import { isTokenProviderReady, setGlobalGetToken } from '@/lib/auth'

import { useAppLoader } from '@/features/app-shell/app-loader-context'
import { UserAvatar } from '@/features/app-shell/sidebar/app-user-avatar'

import { Subtitle, Title } from '@/shared/auth/components/common/card'
import { AuthPendingComponent } from '@/shared/auth/components/common/pending-component'
import { useMe, userQueries } from '@/shared/auth/queries'
import { signOut } from '@/shared/auth/utils/router'

import { Button } from '@/components/ui/button'

const searchSchema = z.object({
	attempt_pathname: z.string(),
	attempt_search: z.string().optional(),
})

export const Route = createFileRoute('/signup/switch-accounts')({
	validateSearch: zodValidator(searchSchema),

	beforeLoad: async ({ context: { clerk } }) => {
		// Initialize token provider for API calls
		if (!isTokenProviderReady() && clerk.auth?.getToken) {
			setGlobalGetToken(clerk.auth.getToken)
		}
	},

	loader: async ({ context: { queryClient } }) => {
		await queryClient.ensureQueryData({
			...userQueries.me(),
			revalidateIfStale: true,
		})
	},

	head: () => ({
		meta: [
			{
				title: "You're Already Signed In | BitLyft AIR®",
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: AuthPendingComponent,
})

function RouteComponent() {
	const search = Route.useSearch()
	const clerk = useClerk()
	const { hideLoader } = useAppLoader()

	const {
		data: { user: me, avatar_url },
	} = useMe()

	useEffect(() => {
		if (me) {
			hideLoader()
		}
	}, [hideLoader, me])

	const handleLogout = async () => {
		await signOut({ clerk })

		const searchParams = new URLSearchParams(search.attempt_search ?? '')

		window.location.href = `${search.attempt_pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
	}

	return (
		<>
			<Title>You're Already Signed In</Title>
			<Subtitle className="text-center">
				You're trying to sign up for a new account while already signed in as:{' '}
				<strong className="font-bold">{me.uname}</strong>
				<br />
				<br />
				Please choose how you'd like to proceed:
			</Subtitle>

			<div className="mt-4 flex flex-col gap-2">
				<Button
					variant="outline"
					className="h-10"
					nativeButton={false}
					render={
						<Link to="/home" className="flex flex-row gap-2">
							<UserAvatar
								user={me}
								avatar={avatar_url ?? ''}
								className="size-6"
							/>
							Continue to dashboard
						</Link>
					}
				/>

				<Button variant="ghost" className="w-full" onClick={handleLogout}>
					Sign out and sign up
				</Button>
			</div>
		</>
	)
}
